import Footer from '@/components/Footer';
import JourneyCard from '@/components/purchase/journey/JourneyCard';
import {
  nonTripOffersLoadingSelector,
  outboundOrInboundJourneysLoadingSelector,
} from '@/features/loading/loadingSelectors';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import type {
  SearchJourneyOffersDTO,
  SearchOffersDTO,
} from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import type { GTM_EVENT } from '@/utils/gtm';
import { getJourneyWarning, getWarningsByTripId } from '@/utils/warning';
import { Button, Icons, Typography } from '@fleet/webshop-ui';
import type { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { type FC, type ReactNode, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';

interface JourneyListProps extends SearchOffersDTO {
  setSelectedJourney: ActionCreatorWithPayload<SearchJourneyOffersDTO>;
  onContinue: () => void;
  onGoBack?: () => void;
  title: ReactNode;
  journeySelectedEvent: GTM_EVENT;
}

const JourneyList: FC<JourneyListProps> = ({
  journeys,
  warnings,
  onContinue,
  onGoBack,
  setSelectedJourney,
  title,
  journeySelectedEvent,
}) => {
  const warningsByTripId = getWarningsByTripId(warnings?.warnings);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const location = useLocation();
  const outboundOrInboundJourneysLoading = useSelector(
    outboundOrInboundJourneysLoadingSelector
  );
  const nonTripOffersLoading = useSelector(nonTripOffersLoadingSelector);
  const isPending = useMemo(
    () => outboundOrInboundJourneysLoading || nonTripOffersLoading,
    [outboundOrInboundJourneysLoading, nonTripOffersLoading]
  );

  return (
    <>
      <div
        className={cn('mx-auto w-full max-w-laptop p-4', {
          ['pb-22']: location.pathname === '/purchase/inbound/journey',
        })}
      >
        <div className="mx-auto max-w-mobile" data-testid="journey-list">
          {title && (
            <Typography
              variant={isLaptopOrBigger ? 'heading1' : 'heading2'}
              className="mb-4"
              as="h1"
            >
              {title}
            </Typography>
          )}
          <div className="grid grid-cols-[1fr_auto] gap-y-4">
            {journeys?.map((journey, key) => (
              <JourneyCard
                key={key}
                journey={journey}
                warningMessage={getJourneyWarning(journey, warningsByTripId)}
                onContinue={onContinue}
                setSelectedJourney={setSelectedJourney}
                journeySelectedEvent={journeySelectedEvent}
              />
            ))}
          </div>
        </div>
      </div>
      {onGoBack && (
        <Footer
          actionButtons={
            <>
              <Button
                size={isLaptopOrBigger ? 'medium' : 'large'}
                data-testid="footer-back-button"
                className="rounded-lg laptop:w-auto"
                variant="tertiary"
                onClick={onGoBack}
              >
                <Icons.arrowLeft />
                <TransText i18nKey="back" />
              </Button>
              <Button
                size={isLaptopOrBigger ? 'medium' : 'large'}
                data-testid="footer-continue-button"
                className="rounded-lg laptop:w-auto"
                onClick={onContinue}
                variant="cta"
                isDisabled
                isPending={isPending}
              >
                <TransText i18nKey="continue" />
                <Icons.arrowRight />
              </Button>
            </>
          }
        />
      )}
    </>
  );
};

export default JourneyList;
